/**
 * useStepAction — resolves what a quest step's affordance should DO.
 *
 * Shared by the two step renderers so they can't drift: `QuestStepList` (the
 * flat card on Discover) and `SegmentJourneyPath` (the Play rail's journey
 * map). The journey map originally shipped without any of this, which left
 * every stop a dead label — no way to link Discord, no way to read what
 * counted. The logic lives here so restoring it to the map couldn't mean
 * maintaining a second copy of the routing rules.
 *
 * Returns null when a step has no affordance: an already-done step, a gated or
 * expired segment, or a plain counter milestone that completes through activity
 * rather than a click.
 */
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { QuestConfigMilestoneResponse } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import { DerivedStep } from "@/hooks/useOnboardingCards";
import { updateShowInviteModal } from "@/redux/meta/metaActions";
import { handleDiscordAuthenticate } from "@/hooks/useDiscordGO";
import { isSafeHref } from "@/widgets/play/safeHref";

/** Capture subtypes that complete through the shared modal rather than a link. */
const CAPTURE_SUBTYPES = ["PHOTO", "CODE_ENTRY", "QR_SCAN"];

export interface StepAction {
    /**
     * `invite` opens the invite modal, `navigate` goes somewhere in-app,
     * `external` leaves the site in a new tab, `complete` raises the shared
     * capture modal. Renderers use this to pick an icon — `run` does the work.
     */
    kind: "invite" | "navigate" | "external" | "complete";
    /** Human-readable intent, for aria-label. */
    label: string;
    /**
     * True when activating this leaves the current page — either to another
     * route or off-site. The stop surfaces it, because a step that silently
     * navigates away from the journey is the least intuitive thing on the card.
     */
    leavesPage: boolean;
    run: () => void;
}

export function useStepAction(): (
    step: DerivedStep,
    milestone: QuestConfigMilestoneResponse | undefined,
    opts: { locked: boolean; expired: boolean; onStartMilestone: (m: QuestConfigMilestoneResponse) => void },
) => StepAction | null {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return (step, milestone, { locked, expired, onStartMilestone }) => {
        if (step.done || locked || expired || !milestone) {
            return null;
        }
        const url = milestone.url;

        /* `action:` sentinels are in-app behaviors, not links. */
        if (url?.startsWith("action:")) {
            if (url.slice("action:".length) === "invite-friend") {
                return {
                    kind: "invite",
                    label: "Invite a friend",
                    leavesPage: false,
                    run: () => dispatch(updateShowInviteModal(true)),
                };
            }
            /*
             * Link Discord IN PLACE. Authored as a `/link-discord` path this
             * navigated the app off the Play page and the journey card
             * vanished mid-step; the OAuth popup keeps the player on the
             * journey. Refetch progression on success so the stop ticks over
             * without the full page reload the settings surface does.
             */
            if (url.slice("action:".length) === "link-discord") {
                return {
                    kind: "invite",
                    label: "Link your Discord account",
                    leavesPage: false,
                    run: () => void handleDiscordAuthenticate(
                        fullToken,
                        () => {
                            toast.success("Discord linked");
                            void queryClient.invalidateQueries({ queryKey: ["quest-progression-user"] });
                            void queryClient.invalidateQueries({ queryKey: ["quest-configs-user"] });
                        },
                        (error: string) => {
                            toast.error(error === "discord_already_linked"
                                ? "That Discord account is already connected to another DevourPlay account."
                                : `Discord linking failed: ${error}`);
                        },
                    ),
                };
            }
            /* Unknown sentinel — no affordance beats a button that does nothing. */
            return null;
        }

        /*
         * Same guard the hero cards use: a step URL is admin-authored, and
         * `//evil.com` passes a bare startsWith("/") check while navigating the
         * player off-site.
         */
        if (url && isSafeHref(url) && url.startsWith("/")) {
            return {
                kind: "navigate",
                label: `Go to ${step.label || "step"} — leaves this page`,
                leavesPage: true,
                run: () => navigate(url),
            };
        }

        /*
         * A URL/SOCIAL milestone logs the click-URL event that credits it (then
         * opens the link); PHOTO/CODE_ENTRY/QR_SCAN raise the capture modal.
         * Both go through the one shared completion path so a Play-page
         * milestone behaves exactly as it does on every other quest surface.
         */
        const subtype = String(milestone.subtype ?? "");
        if (!url && !CAPTURE_SUBTYPES.includes(subtype)) {
            return null;
        }
        /*
         * An http(s) url opens in a NEW TAB (openSocialLink). Distinguished
         * from a capture milestone so the stop can warn that it leaves the
         * site rather than presenting an identical arrow for both.
         */
        const isExternal = !!url && (/^https?:\/\//i).test(url);
        return {
            kind: isExternal ? "external" : "complete",
            label: isExternal
                ? `${step.label || "Step"} — opens in a new tab`
                : `Complete ${step.label || "step"}`,
            leavesPage: isExternal,
            run: () => onStartMilestone(milestone),
        };
    };
}
